import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Grid, Typography, Button, Chip, Alert,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';

import { useLanguage } from '../../contexts/LanguageContext';
import {
  listerPaniers, viderPanier, revaliderPanier, EVENEMENT_PANIER,
  LIGNE_AUTRE_FOURNISSEUR,
} from '../../utils/panier';

/**
 * Les paniers en cours, au-dessus du tableau des commandes.
 *
 * 🔴 UNE CARTE PAR FOURNISSEUR, PAS UN MENU. Le badge dit combien il y en a ;
 * ce bandeau dit lesquels. *On n'ouvre rien, on ne cache rien* — même doctrine
 * que le bandeau des devises en Comptabilité, où mettre une information
 * derrière un clic revient à ne pas la signaler.
 *
 * 🔴 LA REVALIDATION SE FAIT À LA REPRISE, ET ELLE NOMME CE QU'ELLE RETIRE.
 * Un panier vit en `localStorage` : il est **par construction** plus vieux que
 * la base, et il ne le sait pas. Une ligne dont le produit a disparu ou changé
 * de fournisseur est écartée **et dite**. *Un panier qui se répare en silence
 * est un panier qui ment* — c'est le défaut qu'on vient de corriger côté
 * serveur, on ne le réintroduit pas ici.
 */
const BandeauPaniers = ({ produits, fournisseurs, stockId, onReprendre }) => {
  const { t } = useLanguage();
  const [paniers, setPaniers] = useState([]);
  const [retirees, setRetirees] = useState([]);

  useEffect(() => {
    const relire = () => setPaniers(listerPaniers(stockId));
    relire();
    window.addEventListener(EVENEMENT_PANIER, relire);
    window.addEventListener('storage', relire);
    return () => {
      window.removeEventListener(EVENEMENT_PANIER, relire);
      window.removeEventListener('storage', relire);
    };
  }, [stockId]);

  if (paniers.length === 0) return null;

  const nomFournisseur = (id) =>
    (fournisseurs || []).find((f) => String(f.id) === String(id))?.company_name
    || t('suppliers.notSpecified');

  const reprendre = (panier) => {
    // ⚠️ ON REVALIDE AVANT D'OUVRIR, pas après. Ouvrir un formulaire avec des
    // lignes que le serveur refusera ferait découvrir le problème au moment de
    // l'enregistrement — c'est-à-dire le plus tard possible, et sur un écran
    // qui n'explique pas pourquoi.
    const { lignes, retirees: ecartees } = revaliderPanier(panier, produits);
    setRetirees(ecartees);
    onReprendre({ supplier_id: panier.supplier_id, lignes }, ecartees);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {retirees.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRetirees([])}>
          {t('cart.linesRemoved')}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {retirees.map((ligne) => (
              <li key={ligne.product_id}>
                {ligne.product_name || `#${ligne.product_id}`} —{' '}
                {ligne.raison === LIGNE_AUTRE_FOURNISSEUR
                  ? t('cart.reasonOtherSupplier')
                  : t('cart.reasonNotFound')}
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Grid container spacing={2}>
        {paniers.map((panier) => (
          <Grid item xs={12} md={4} key={panier.supplier_id}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex',
                         flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                  {nomFournisseur(panier.supplier_id)}
                </Typography>
                {/* Le nombre de LIGNES vit ici, sur la carte du fournisseur — là
                    où il est actionnable. Le badge, lui, compte les paniers :
                    deux compteurs, deux questions différentes. */}
                <Chip size="small" label={panier.lignes.length} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                <Button size="small" variant="contained" sx={{ flex: 1 }}
                        onClick={() => reprendre(panier)}>
                  {t('cart.resume')}
                </Button>
                <Button size="small" color="inherit" startIcon={<DeleteOutlineIcon />}
                        onClick={() => viderPanier(stockId, panier.supplier_id)}>
                  {t('cart.empty')}
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default BandeauPaniers;
